<template>
  <div class="audit-log-view">
    <page-header title="Audit Log" />

    <align flex collapse-end direction="flex-start">
      <div class="search-container">
        <search-area
          v-model="query"
          class="input"
        />
      </div>

      <align small-gap>
        <pill-dropdown
          v-model="type"
          :options="typeFilterOptions"
        />

        <pill-dropdown
          v-if="isAdmin"
          v-model="employee"
          :options="employeeFilterOptions"
        />
      </align>
    </align>

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template>
        <div class="list">
          <audit-log-item
            v-for="log in logs"
            :key="log.id"
            :log="log"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import debounce from 'lodash.debounce';

import { REPORT_TYPE_OPTIONS } from '@cardladder/shared/constants';

import AuditLogItem from './AuditLogItem';

import { searchService } from '@/services';

export default {
  components: {
    AuditLogItem,
  },

  data() {
    return {
      limit: 100,
      loading: false,
      loaded: false,
      logs: [],
      page: 1,
      hasMoreData: true,
      employee: 'all',
      type: 'all',
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);

    if (this.$route.query.employee) {
      this.employee = this.$route.query.employee;
    }

    this.reset();
  },

  watch: {
    query() {
      this.reset();
    },

    employee() {
      this.reset();
    },

    type() {
      this.reset();
    },

    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      employeeOptions: (state) => state.employeeOptions,
    }),

    ...mapGetters({
      isAdmin: 'isAdmin',
    }),

    typeFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...REPORT_TYPE_OPTIONS,
      ];
    },

    employeeFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...this.employeeOptions,
      ];
    },

    query: {
      get() {
        return this.$store.state.auditLogQuery;
      },

      set(value) {
        this.$store.commit('SET_AUDIT_LOG_QUERY', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.logs.length === 0;
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;

      this.search(true);
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const filters = {};

        if (!this.isAdmin) {
          filters.uid = this.user.uid;
        } else if (this.employee !== 'all') {
          filters.uid = this.employee;
        }

        if (this.type !== 'all') {
          filters.type = this.type;
        }

        const data = await searchService.search({
          index: 'auditlog',
          query: this.query,
          page: this.page,
          limit: this.limit,
          filters,
        });

        if (reset) {
          this.logs = data.hits;
        } else {
          this.logs = [...this.logs, ...data.hits];
        }

        if (data.hits.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.audit-log-view {
  @include container;
  min-height: 100%;
}

.results {
  position: relative;
  flex: 1;
}

.list {
  margin-top: $gp;

  @media($medium-down) {
    margin: $gp (-$gp) 0;
  }
}

.num-results {
  margin-bottom: $gp * .25;
}

.search-container {
  margin-bottom: $gp * .5;

  @media($medium-down) {
    width: 100%;
  }
}

</style>
